import { useEffect, useState } from "react";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { queries } from "@corpuscomplete/core/zero";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { PopoverContent } from "@corpuscomplete/web-ui/components/popover";
import { Text } from "@corpuscomplete/web-ui/components/text";

const macroKeys: Nutrient.Key[] = ["energy", "carbohydrate", "protein", "fat"];

export function SlotTargetsPopoverContent({
  open,
  onOpenChange,
  initialTargets,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTargets: Nutrient.Targets | null;
  onSave: (targets: Nutrient.Targets | null) => void;
}) {
  const [nutrients] = useQuery(queries.nutrient.hasKey());
  const [inputs, setInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      return;
    }
    const next: Record<string, string> = {};
    if (initialTargets) {
      for (const [key, target] of Object.entries(initialTargets)) {
        if (target?.type === "exact") {
          next[key] = target.value.toString();
        }
      }
    }
    setInputs(next);
  }, [initialTargets, open]);

  return (
    <PopoverContent align="start" className="max-h-[80vh] w-80 overflow-y-auto p-3">
      <div className="flex flex-col gap-2">
        {macroKeys.map((key) => (
          <TargetInput
            key={key}
            label={toLabel(key)}
            unit={findUnit(nutrients, key)}
            value={inputs[key] ?? ""}
            onChange={(value) => setInputs((prev) => ({ ...prev, [key]: value }))}
          />
        ))}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setInputs({});
            const merged = NutrientUI.mergeExactTargetInputs({
              existingTargets: initialTargets,
              inputs: {},
              keys: macroKeys,
            });
            if (merged.ok) {
              onSave(merged.value);
            }
            onOpenChange(false);
          }}
        >
          <Text variant="xs">Clear</Text>
        </Button>
        <Button
          onClick={() => {
            const merged = NutrientUI.mergeExactTargetInputs({
              existingTargets: initialTargets,
              inputs,
              keys: macroKeys,
            });
            if (merged.ok) {
              onSave(merged.value);
              onOpenChange(false);
            }
          }}
        >
          <Text variant="xs">Save</Text>
        </Button>
      </div>
    </PopoverContent>
  );
}

function TargetInput({
  label,
  unit,
  value,
  onChange,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <Text className="w-28 text-muted-foreground">{label}</Text>
      <Input
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8"
      />
      <Text className="w-12 text-right text-muted-foreground">{unit}</Text>
    </label>
  );
}

function toLabel(key: Nutrient.Key): string {
  return key === "energy" ? "Energy" : `${key[0]?.toUpperCase()}${key.slice(1)}`;
}

function findUnit(
  nutrients: ReadonlyArray<{
    key?: Nutrient.Key | null;
    unit?: { defaultName: string } | null;
  }>,
  key: Nutrient.Key,
): string {
  return nutrients.find((nutrient) => nutrient.key === key)?.unit?.defaultName ?? "";
}
